import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export function useUrlSyncedFilters<T extends object>(
  defaults: T,
  serialize: (state: T) => URLSearchParams,
  parse: (params: URLSearchParams) => Partial<T>
): [T, Dispatch<SetStateAction<T>>] {
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();

  const state = useMemo(
    () => ({ ...defaults, ...parse(urlSearchParams) }),
    [defaults, parse, urlSearchParams]
  );

  const setState = useCallback<Dispatch<SetStateAction<T>>>(
    (update) =>
      setUrlSearchParams(
        (prev) => {
          const current = { ...defaults, ...parse(prev) };
          const next =
            typeof update === 'function'
              ? (update as (prevState: T) => T)(current)
              : update;
          return serialize(next);
        },
        { replace: true }
      ),
    [defaults, parse, serialize, setUrlSearchParams]
  );

  return [state, setState];
}

export default useUrlSyncedFilters;
